import React, { FormEventHandler } from "react";
import { Button, SimpleGrid } from "@mantine/core";
import {
  useStripe,
  useElements,
  PaymentElement,
  AddressElement,
} from "@stripe/react-stripe-js";
import { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import logger from "../utils/logger";
import { useTrackForm } from "../hooks/useTrackForm";

interface Props {
  returnToUrl: string;
  paymentMethodId?: string;
  cols?: number;
  buttonText?: string;
}

const PaymentMethodForm: React.FC<Props> = ({
  returnToUrl,
  paymentMethodId,
  cols,
  buttonText,
}) => {
  const { markDirty, trackSubmit, trackFailure } =
    useTrackForm("payment_method");
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = React.useState(false);
  const [addressState, setAddressState] = React.useState<
    StripeAddressElementChangeEvent | undefined
  >(undefined);

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setLoading(true);
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: returnToUrl,
          payment_method: paymentMethodId,
          payment_method_data: {
            billing_details: {
              name: addressState?.value.name,
            },
          },
        },
      });
      // On success Stripe redirects to `return_url`, so this only resumes for a failure
      // (or a payment method that needs no redirect). Completions are counted by the
      // API's `payment_method_added`, raised from the Stripe webhook.
      if (error) {
        trackFailure({ error_type: error.type });
      } else {
        trackSubmit();
      }
    } catch (error) {
      trackFailure();
      logger.exception("stripe setup confirmation failed", error);
    }
    setLoading(false);
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <SimpleGrid cols={{ sm: 1, md: cols || 2 }}>
          <PaymentElement
            options={{}}
            id="payment-element"
            onChange={(e) => {
              if (!e.empty) markDirty();
            }}
          />
          <AddressElement
            options={{ mode: "billing" }}
            id="address-element"
            onChange={(e) => {
              if (!e.empty) markDirty();
              setAddressState(e);
            }}
          />
        </SimpleGrid>
        <Button
          id="btn-save-payment-method"
          fullWidth
          mt="md"
          type="submit"
          disabled={!stripe}
          loading={loading}
        >
          {buttonText || "Save"}
        </Button>
      </form>
    </div>
  );
};

export default PaymentMethodForm;
